import React, { useState } from "react";
import { Table } from "../common/Table/index.jsx";
import GridSelect from '../common/GridSelect.jsx';
import { Button } from "../bootstrap/index.jsx";
import { Adalicon } from "../common/Icons.jsx";
import NumberInput from "../common/NumberInput.jsx";
import FormattedNumber from "../common/FormattedNumber.jsx";
import { castDataType, DataType } from "../form_editor/common.jsx";
import TextInput from "../common/TextInput.jsx";

// Checks values for dimensions for user created options (creatable).
// Matches all string values as user created (id's are stored for predefined categories)
const resolveInitialCreatedOptions = (rows)  => {
  let initialOptions = {}
  rows.forEach(r => {
    Object.keys(r).forEach(key => {
      let matches = key.split(/^dim\-/g) 
      // Skip other than dimensions
      if (matches.length > 1 && typeof r[key] == 'string'){
        var scope = matches.pop()
        initialOptions[scope] = initialOptions[scope] || []
        // Add only if not already handled
        if (initialOptions[scope].findIndex(o => o.value == r[key]) == -1) {
          initialOptions[scope].push({value: r[key], label: r[key]})
        }
      }
    })
  })
  return initialOptions
}

export const ViewModeValue = ({mapping, value}) => {
  return <span>{mapping[value] ? mapping[value] : value}</span>
}

const BudgetingGrid = ({ rows, columns: propColumns, dimensions, readOnly, deleteRow, updateRow, fullWidth, viewMode, onClientHeightChange, getColumnAggregateValue, calculateAggregateColumnFormula, stateKey, selectedDimensions, groupByColumn = null }) => {
  const [creatableOptions, setCreatableOptions] = useState(resolveInitialCreatedOptions(rows));

  const addCreatableOption = (scope, v) => {
    let newOptions = [...(creatableOptions[scope] || [])] 
    newOptions.push({value: v, label: v});
    setCreatableOptions({...creatableOptions, [scope]: newOptions});
  };

  const allColumns = groupByColumn == null ? propColumns : propColumns.filter((col) => col.key != groupByColumn);

  const columns = React.useMemo(
    () => { 
      let columnDefs = [
        {
          id: "total",
          Footer: I18n.t("total"),
          columns: [
            groupByColumn ? {
              Header: propColumns.find(c => c.key == groupByColumn).label,
              accessor: `col-${groupByColumn}`,
              classes: viewMode ? '' : 'editable-cell add',
              defaultCanSort: true,
              widthOffset: viewMode ? 0 : 10, // Dropdown arrow
            } : null,
            ...dimensions
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((d, i) => ({
                Header: d.name,
                accessor: `dim-${d.id}`,
                classes: viewMode ? '' : 'editable-cell add',
                // disableSortBy: true,
                defaultCanSort: true,
                sortType: (rowA, rowB, columnId, desc) => {
                  const mapping= Object.fromEntries(d.categories.map((c) => [c.category_id, c.category_name]))
                  const labelA = mapping[rowA.values[columnId] || {}] || "";
                  const labelB = mapping[rowB.values[columnId] || {}] || "";
                
                  return labelA.localeCompare(labelB);
                },
                widthOffset: viewMode ? 0 : 10, // Dropdown arrow
                cellText: (value) => {
                  if (!value) { return d.creatable_categories ? I18n.t("react_select_creatable_placeholder") : I18n.t("react_select_placeholder") }
                  return d.categories.find((cat) => cat.category_id == value)?.category_name || value
                },
                Cell: ({ value, updateRow, row }) => {
                  return viewMode ? (
                    <ViewModeValue value={value} mapping={Object.fromEntries(d.categories.map((c) => [c.category_id, c.category_name]))} />
                  ) : (
                    <GridSelect
                      value={value}
                      disabled={readOnly}
                      creatable={d.creatable_categories}
                      placeholder={d.creatable_categories ? I18n.t("react_select_creatable_placeholder") : I18n.t("react_select_placeholder")}
                      addCreatableOption={(v) => addCreatableOption(d.id, v)}
                      creatableOptions={creatableOptions[d.id] || []}
                      onChange={(value) => updateRow(row.original.id, `dim-${d.id}`, value)}
                      options={d.categories.map((c) => ({ value: c.category_id, label: c.category_name }))}
                      menuPortalTarget={document.body} // Use body as portal, since inside a overflow hidden relative container
                      styles={{ input: (provided, state) => ({ ...provided, lineHeight: "1rem" }) }} // Override line height set to the grid
                      className="w-100"
                    />
                  );
                },
              })),
            ...allColumns.map((c) => ({
              Header: c.label,
              accessor: c.strId,
              classes: viewMode ? '' : 'editable-cell add',
              disableSortBy: true,
              aggregate: c.formula ? (leafValues, aggregatedValues) => calculateAggregateColumnFormula(c, leafValues) : (c.type == 'text' ? (leafValues, aggregatedValues)=> '-' : "sum"),
              aggregateValue: c.formula ? getColumnAggregateValue : undefined,
              Aggregated: ({ value, row }) => (
                // Multi-level useGroupBy doesn't work with V7, has to do the aggregation again here. https://github.com/TanStack/table/issues/3323#issuecomment-867488865
                <b>
                  <FormattedNumber round={3} number={c.formula ? calculateAggregateColumnFormula(c, row.leafRows.map(l => l.values)) : value} />
                </b>
              ),
              Cell: ({ value, updateRow, row: { original } }) =>
                { 
                  let r;
                  if(viewMode){
                  c.type == 'text' 
                    ? r = value || ''
                    : r = <FormattedNumber round={3} number={original[`${c.strId}-error`] || value} />
                  } else {
                    switch(c.type) {
                      case 'text':
                        r = <TextInput defaultValue={value} readOnly={readOnly} onBlur={(e) => updateRow(original.id, c.strId, e.target.value, true)} />
                        break;
                      case 'input':
                        r = <NumberInput defaultValue={value} readOnly={readOnly} onBlur={(e) => updateRow(original.id, c.strId, e.target.value, true)} />
                        break;
                      case 'calculation': 
                        r = <FormattedNumber round={3} number={original[`${c.strId}-error`] || value} />
                        break;
                      default:
                        r = <FormattedNumber round={3} number={original[`${c.strId}-error`] || value} />
                    }
                  }
                  return r;
                }
            })),
          ].filter((c) => c),
        },
      ];

        columnDefs.push({
          id: "total_value",
          Footer: ({ rows }) => React.useMemo(() => <FormattedNumber round={3} number={rows.reduce((sum, row) => (castDataType(row.values.value, DataType.DECIMAL) || 0) + sum, 0)} />, [rows]),
          columns: [
            ...(!viewMode ? [{
              Header: <div className="visibility-hidden">-</div>,
              accessor: "id",
              classes: "actions",
              disableSortBy: true,
              minWidth: 40,
              width: 40,
              maxWidth: 40,
              disableResizing: true,
              Cell: ({ value: id, deleteRow }) => (
                <>
                  <Button elemType="button" size="xs" type="danger" disabled={readOnly} onClick={() => deleteRow(id)}>
                    <Adalicon type="trash" />
                  </Button>
                </>
              ),
            }] : []),
          ],
        })

      return columnDefs

    },
    [dimensions, readOnly, fullWidth, creatableOptions, viewMode, allColumns]
  );

  const filteredDimensions = dimensions.sort((a,b) => a.sort_order - b.sort_order).filter((d) => selectedDimensions.includes(d.id)).map((d)=> `dim-${d.id}`);
  const filteredColumns = groupByColumn ? [`col-${groupByColumn}`, ...filteredDimensions] : filteredDimensions;

  const groupBy = React.useMemo(() => (viewMode ? filteredColumns : []), [viewMode, dimensions]);
  const sortBy = groupBy.map((g) => ({id: g, desc: false}))
  return <Table 
    data={rows} 
    columns={columns} 
    isFixedSize 
    showFooter 
    useGrouping 
    useExpand 
    useSortBy
    tableProps={
      {deleteRow, updateRow}
    }
    defaultGroupBy={{groupBy: groupBy}}
    defaultSortBy= {sortBy}
    alwaysExpanded={true}
    onClientHeightChange={onClientHeightChange}
    stateKey={stateKey}
  />
};

export default BudgetingGrid;
